import stringEscape from 'js-string-escape';

/**
 * @description made to check axios parameters, all 'empty' values will be replaced with 'undefined', and 'undefined' is filtered by axios itself and is not sent
 * @param val
 * @returns {val | undefined}
 */
export const checkRequestParameter = (val) => {
  if (isNull(val) || isUndef(val) || val === '' || (isArr(val) && !val.length)) {
    return undefined;
  }
  return val;
};

/**
 * @description made to sort countries by custom order
 * @param countries
 * @param byField
 * @returns {Array}
 */
export const sortCountriesBy = (countries, byField = 'code') => {
  return [...countries].sort((a, b) => {
    if (a[byField] === undefined) {
      return 1;
    }
    if (b[byField] === undefined) {
      return -1;
    }
    return a[byField].localeCompare(b[byField]); // sort alphabetically
  });
}

/**
 * @description Is null
 * @param val
 * @returns {boolean}
 */
export const isNull = (val) => val === null;

/**
 * @description escapes a search string for the API, preserving null for unset filters
 * @param search
 * @returns {string | null}
 */
export const escapeSearch = (search) => isNull(search) ? null : stringEscape(search);

/**
 * @description Is array
 * @param val
 * @returns {boolean}
 */
export const isArr = (val) => Array.isArray(val);

/**
 * @description Is object
 * @param val
 * @returns {boolean}
 */
export const isObj = (val) =>
  typeof val === 'object' && !isArr(val) && !isNull(val);

/**
 * @description Is number
 * @param val
 * @returns {boolean}
 */
// eslint-disable-next-line no-restricted-globals
export const isNum = (val) => typeof val === 'number' && !isNaN(val);

/**
 * @description Is function
 * @param val
 * @returns {boolean}
 */
export const isFunc = (val) => typeof val === 'function';

/**
 * @description Is string
 * @param val
 * @returns {boolean}
 */
export const isStr = (val) => typeof val === 'string';

/**
 * @description Is undefined
 * @param val
 * @returns {boolean}
 */
export const isUndef = (val) => typeof val === 'undefined';

/**
 * @description Is boolean
 * @param val
 * @returns {boolean}
 */
export const isBool = (val) => typeof val === 'boolean';

/**
 * @description To int
 * @param val
 * @returns {number}
 */
export const toInt = (val) => parseInt(val, 10);

/**
 * @description separateEvery3Digits
 * @param {number} val
 * @returns {string}
 */
export const separateEvery3Digits = (val) => Number.parseFloat(val).toLocaleString('us-US');
/**
 * @description Is non empty string
 * @param val
 * @returns {boolean|boolean}
 */
export const isNonEmptyStr = (val) => isStr(val) && val !== '';

/**
 * @description Is non empty array
 * @param val
 * @returns {boolean|boolean}
 */
export const isNonEmptyArr = (val) => isArr(val) && val.length > 0;

/**
 * @description Has property
 * @param obj
 * @param prop
 * @returns {boolean}
 */
export const hasProp = (obj, prop) =>
  Object.prototype.hasOwnProperty.call(obj, prop);

/**
 * @description Has method
 * @param obj
 * @param method
 * @returns {boolean}
 */
export const hasMethod = (obj, method) =>
  hasProp(obj, method) && isFunc(obj[method]);

/**
 * @description Get object keys
 * @param obj
 * @returns {Array}
 */
export const getKeys = (obj) => isObj(obj) ? Object.keys(obj) : [];

/**
 * @description Has key
 * @param obj
 * @param key
 * @returns {boolean}
 */
export const hasKey = (obj, key) => getKeys(obj).indexOf(key) > -1;

/**
 * @description Has keys
 * @param obj
 * @returns {boolean}
 */
export const hasKeys = (obj) => getKeys(obj).length > 0;

/**
 * @description Each key
 * @param obj
 * @param callback
 */
export const eachKey = (obj, callback) => {
  getKeys(obj).forEach((key, index) => callback(key, obj[key], index));
};

/**
 * @description Each prop
 * @param obj
 * @param callback
 */
export const eachProp = (obj, callback) => {
  eachKey(obj, (key, prop, index) => callback(prop, key, index));
};

/**
 * @description Capitalize
 * @param text
 * @returns {string}
 */
export const capitalize = (text) =>
  `${text.charAt(0).toUpperCase()}${text.slice(1).toLowerCase()}`;

/**
 * @description Parse to type
 * @param val
 * @returns {*}
 */
export const parseToType = (val) => {
  if (!isStr(val)) {
    return undefined;
  }

  if (val === 'true') {
    return true;
  }

  if (val === 'false') {
    return false;
  }

  if (val === 'null') {
    return null;
  }

  if (+val + '' === val) {
    return +val;
  }

  return val;
};

/**
 * @description Extract nexted prop
 * @param obj
 * @param keysText
 * @returns {*}
 */
export const extractNestedProp = (obj, keysText) => {
  const keys = keysText.split('.');
  const keysLength = keys.length - 1;
  let keysIndex = 0;
  let isValidKey = true;
  let targetObj = { ...obj };
  let targetProp;
  let nextTarget;

  if (keys.length > 0) {
    while (isValidKey) {
      nextTarget = targetObj[keys[keysIndex]];

      if (keysIndex === keysLength) {
        targetProp =
          !isUndef(nextTarget) && !isNull(nextTarget) ? nextTarget : undefined;
        break;
      }

      if (!isObj(nextTarget)) {
        isValidKey = false;
        break;
      }

      targetObj = nextTarget;
      keysIndex += 1;
    }
  }

  return targetProp;
};

/**
 * @description Debounce
 * @returns {function}
 */
export const debounce = ({ timeout, id }) => {
  const timers = {};

  return (callback) => {
    if (timers[id]) {
      clearTimeout(timers[id]);
    }
    timers[id] = setTimeout(callback, timeout);
  };
};

/**
 * @description Sequence
 * @returns {{chain: (function(*=): chain), execute: execute}}
 */
// eslint-disable-next-line func-names
export const sequence = function () {
  const chained = [];
  let value;
  let error;

  // eslint-disable-next-line func-names
  const chain = function (func) {
    if (chained) {
      chained.push(func);
    }
    return this;
  };

  // eslint-disable-next-line func-names
  const execute = function (index = 0) {
    let callback;
    if (!chained || index >= chained.length) {
      return true;
    }

    // eslint-disable-next-line prefer-const
    callback = chained[index];
    callback({
      resolve(_value) {
        value = _value;
        // eslint-disable-next-line no-param-reassign,no-plusplus
        execute(++index);
      },
      reject(_error) {
        error = _error;
        // eslint-disable-next-line no-param-reassign,no-plusplus
        execute(++index);
      },
      response: {
        value,
        error,
      },
    });
  };

  return {
    chain,
    execute,
  };
};

/**
 * @desc Hide page scroll
 */
export const hidePageScroll = () => {
  document.documentElement.classList.add('hide-scroll');
};

/**
 * @description Show page scroll
 */
export const showPageScroll = () => {
  document.documentElement.classList.remove('hide-scroll');
};

/**
 * @description Open url in new window
 * @param urlWindow
 * @param url
 */
export const openUrlInNewWindow = (urlWindow, url) => {
  if (urlWindow) {
    /* eslint-disable-next-line no-param-reassign */
    urlWindow.opener = null;
    /* eslint-disable-next-line no-param-reassign */
    urlWindow.location = url;
  } else {
    console.log('urlWindow is null, cannot set opener or location');
  }
};

/**
 * @description Concatenates two arrays and removes duplicates by 'id'
 * accepts array1 and array2 as second and third parameters
 */
export const concatArrays = (p, ...arrs) =>
  []
    .concat(...arrs)
    .reduce(
      (a, b) => (!a.filter((c) => b[p] === c[p]).length ? [...a, b] : a),
      [],
    );

/**
 * @description Gets navigator language
 */
export const getNavigatorLanguage = () =>
  navigator.languages && navigator.languages.length
    ? navigator.languages[0]
    : navigator.userLanguage ||
      navigator.language ||
      navigator.browserLanguage ||
      'en';

/**
 * @description convert camelCase to kebab-case
 * @param {string} str
 * @returns {string}
 */
export const camelToKebabCase = (str) => str.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();

/**
 * @description Trigger a file download in the browser using native APIs
 * @param {BlobPart} data
 * @param {string} filename
 * @param {string} [mimeType]
 */
/**
 * A legacy invoice file name carries an md5 the user should never see
 * (`DEPB2600451_75423663….pdf`). Business Central names carry none, and stripping from a
 * `-1` index would eat the whole name and save the file as `.pdf`.
 */
export const invoiceFileName = (contentDisposition) => {
  const match = (contentDisposition || '').match(/filename=(.+\.[a-z]+)/);

  if (!match) {
    return 'invoice.pdf';
  }

  const fileName = match[1];
  const underscore = fileName.indexOf('_');

  if (underscore === -1) {
    return fileName;
  }

  return fileName.replace(fileName.substring(underscore, fileName.lastIndexOf('.')), '');
};

export const downloadFile = (data, filename, mimeType) => {
  const blob = mimeType ? new Blob([data], { type: mimeType }) : new Blob([data]);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * @description Time options for a time select, e.g. ['00:00', '00:15', …, '23:45']
 * @param {{ step?: number, hasEndOfDay?: boolean }} options - hasEndOfDay appends '23:59'
 * @returns {string[]}
 */
export const getTimeOptions = ({ step = 15, hasEndOfDay = false } = {}) => {
  const pad = (val) => String(val).padStart(2, '0');
  const options = [];

  for (let minutes = 0; minutes < 24 * 60; minutes += step) {
    options.push(`${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`);
  }

  if (hasEndOfDay) {
    options.push('23:59');
  }

  return options;
};
